import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, updateDoc, doc, increment } from 'firebase/firestore';
import { db } from '../firestore';
import { Notification } from '../types/global';
import { X, Info, AlertTriangle, CheckCircle, XCircle, Megaphone, Tag } from 'lucide-react';
import '../styles/NotificationBanner.css';
import { toJsDate } from '../utils/safeDate';

interface NotificationBannerProps {
  currentPage: string;
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({ currentPage }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayedNotifications, setDisplayedNotifications] = useState<string[]>([]);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);
  const [scrollTriggeredNotifications, setScrollTriggeredNotifications] = useState<string[]>([]);

  useEffect(() => {
    fetchActiveNotifications();
    
    // Load dismissed notifications from localStorage. A corrupt value would throw
    // here on EVERY page (this banner is global), so parse defensively and self-heal
    // by clearing the bad key rather than white-screening the whole site.
    const dismissed = localStorage.getItem('dismissedNotifications');
    if (dismissed) {
      try {
        const parsed = JSON.parse(dismissed);
        if (Array.isArray(parsed)) setDismissedNotifications(parsed);
      } catch {
        localStorage.removeItem('dismissedNotifications');
      }
    }
  }, [currentPage]);

  // Timer system for controlled notification display with custom durations
  useEffect(() => {
    if (notifications.length === 0) return;

    const visibleNotifications = notifications.filter(notification => 
      !dismissedNotifications.includes(notification.id)
    );

    if (visibleNotifications.length === 0) return;

    const currentNotification = visibleNotifications[currentNotificationIndex % visibleNotifications.length];
    
    // Use custom duration or default to 8 seconds
    const duration = currentNotification?.displayDuration || 8000;
    
    // Only auto-rotate if autoRotate is not explicitly false
    const shouldAutoRotate = currentNotification?.autoRotate !== false;
    
    if (!shouldAutoRotate || currentNotification?.stickyDisplay) {
      return; // Don't auto-rotate this notification
    }

    const timer = setInterval(() => {
      setCurrentNotificationIndex(prev => {
        const next = (prev + 1) % visibleNotifications.length;
        return next;
      });
    }, duration);

    return () => clearInterval(timer);
  }, [notifications.length, currentNotificationIndex, dismissedNotifications]);

  // Scroll detection for scroll-triggered notifications
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      notifications.forEach(notification => {
        if (
          notification.showOnScroll && 
          notification.scrollTriggerPercentage &&
          scrollPercentage >= notification.scrollTriggerPercentage &&
          !scrollTriggeredNotifications.includes(notification.id) &&
          !dismissedNotifications.includes(notification.id)
        ) {
          setScrollTriggeredNotifications(prev => [...prev, notification.id]);
          
          // Show this notification immediately
          const notificationIndex = notifications.findIndex(n => n.id === notification.id);
          if (notificationIndex >= 0) {
            setCurrentNotificationIndex(notificationIndex);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [notifications, scrollTriggeredNotifications, dismissedNotifications]);

  const fetchActiveNotifications = async () => {
    try {
      const now = new Date();
      
      // Query for active notifications
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('active', '==', true)
      );
      
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      
      // Filter notifications based on schedule and target pages
      const activeNotifications = notificationsData.filter(notification => {
        // Check if notification is within active time range
        const startDate = toJsDate(notification.startDate);
        const endDate = toJsDate(notification.endDate);

        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
        
        // Check if notification targets this page (empty/absent targetPages = all pages).
        // Guard the field: an older notification doc without targetPages would throw
        // `undefined.length`, aborting the whole filter so NO banner shows anywhere.
        if ((notification.targetPages?.length ?? 0) > 0 &&
            !notification.targetPages?.includes(currentPage)) {
          return false;
        }
        
        // Check if notification has reached max displays
        if (notification.maxDisplays && 
            (notification.displayCount || 0) >= notification.maxDisplays) {
          return false;
        }
        
        return true;
      });
      
      // Sort by priority
      const priorityOrder: { [key: string]: number } = { urgent: 0, high: 1, medium: 2, low: 3 };
      activeNotifications.sort((a, b) => {
        const aPriority = priorityOrder[a.priority] ?? 3; // default to low priority (3) if not found
        const bPriority = priorityOrder[b.priority] ?? 3; // default to low priority (3) if not found
        return aPriority - bPriority;
      });
      
      setNotifications(activeNotifications);
      
      // Increment display count for shown notifications
      activeNotifications.forEach(async (notification) => {
        try {
          const notificationRef = doc(db, 'notifications', notification.id);
          await updateDoc(notificationRef, {
            displayCount: increment(1)
          });
        } catch (error) {
          console.error('Error updating display count:', error);
        }
      });
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissNotification = (notificationId: string) => {
    const newDismissed = [...dismissedNotifications, notificationId];
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify(newDismissed));
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'info': return <Info size={20} />;
      case 'warning': return <AlertTriangle size={20} />;
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <XCircle size={20} />;
      case 'announcement': return <Megaphone size={20} />;
      case 'promotion': return <Tag size={20} />;
      default: return <Info size={20} />;
    }
  };

  const getNotificationStyles = (type: string, priority: string) => {
    const baseStyles = 'notification-banner';
    const typeClass = `notification-${type}`;
    const priorityClass = `priority-${priority}`;
    
    return `${baseStyles} ${typeClass} ${priorityClass}`;
  };

  const visibleNotifications = notifications.filter(notification => 
    !dismissedNotifications.includes(notification.id)
  );

  if (isLoading || visibleNotifications.length === 0) {
    return null;
  }

  // Only show one notification at a time
  const currentNotification = visibleNotifications[currentNotificationIndex % visibleNotifications.length];
  
  if (!currentNotification) {
    return null;
  }

  return (
    <div className="notification-banners-container">
      <div 
        key={currentNotification.id} 
        className={`${getNotificationStyles(currentNotification.type, currentNotification.priority)} notification-slide-in`}
      >
          <div className="notification-content">
            <div className="notification-icon">
              {getNotificationIcon(currentNotification.type)}
            </div>
            
            <div className="notification-text">
              <h4 className="notification-title">{currentNotification.title}</h4>
              <p className="notification-message">{currentNotification.message}</p>
            </div>
            
            {currentNotification.buttonText && currentNotification.buttonLink && (
              <div className="notification-action">
                <a 
                  href={currentNotification.buttonLink}
                  className="notification-button"
                  target={currentNotification.buttonLink.startsWith('http') ? '_blank' : '_self'}
                  rel={currentNotification.buttonLink.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  {currentNotification.buttonText}
                </a>
              </div>
            )}
          </div>
          
          {currentNotification.dismissible && (
            <button 
              className="notification-dismiss"
              onClick={() => dismissNotification(currentNotification.id)}
              aria-label="Dismiss notification"
            >
              <X size={18} />
            </button>
          )}

          {/* Notification indicator dots */}
          {visibleNotifications.length > 1 && (
            <div className="notification-indicators">
              {visibleNotifications.map((_, index) => (
                <button
                  key={index}
                  className={`notification-dot ${index === currentNotificationIndex % visibleNotifications.length ? 'active' : ''}`}
                  onClick={() => setCurrentNotificationIndex(index)}
                  aria-label={`Show notification ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
    </div>
  );
};

export default NotificationBanner;